import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";

// Per-row widths for the fake username / email lines — a roster reads as
// handles and addresses of different lengths, not one repeated bar.
const ROW_SHAPES = [
  ["w-28", "w-44"],
  ["w-36", "w-52"],
  ["w-24", "w-40"],
  ["w-32", "w-48"],
  ["w-40", "w-56"],
  ["w-28", "w-44"],
  ["w-36", "w-40"],
  ["w-24", "w-52"],
];

// Toolbar segments: search box, then the filter buttons (Status, Issues,
// View, the funnel) — same slots CreatorsTable's real toolbar fills.
const TOOLBAR_BUTTONS = ["w-16", "w-16", "w-14", "w-7.5"];

// Header + cell widths per column after the username column: campaign,
// inbox, three metric columns, status, and a couple of CRM fields.
const COLUMNS = [
  { head: "w-20", cell: "w-40" },
  { head: "w-12", cell: "w-44" },
  { head: "w-16", cell: "w-10" },
  { head: "w-16", cell: "w-12" },
  { head: "w-20", cell: "w-12" },
  { head: "w-12", cell: "w-32" },
  { head: "w-14", cell: "w-36" },
  { head: "w-20", cell: "w-28" },
];

interface CreatorsTableSkeletonProps {
  /** Fake rows — enough to fill a tall pane; the container clips the rest. */
  rows?: number;
}

/**
 * Full-height stand-in for CreatorsTable while the first page loads:
 * the toolbar strip, the sticky header row, avatar + two-line username
 * rows with cell bars, and the pagination footer — same heights and borders
 * as the real table, so the swap to data doesn't shift anything.
 */
export default function CreatorsTableSkeleton({ rows = 14 }: CreatorsTableSkeletonProps) {
  return (
    <div className="flex h-full flex-col bg-background" aria-busy="true" aria-label="Loading creators animate-out fade-out duration-200">
      {/* toolbar */}
      <div className="flex h-11 shrink-0 items-stretch border-b">
        <div className="flex min-w-40 max-w-80 flex-1 items-center gap-2 px-2.5">
          <Skeleton className="size-4 rounded-full" />
          <Skeleton className="h-3 w-40" />
        </div>
        <div className="flex items-center gap-2 border-l px-2">
          {TOOLBAR_BUTTONS.map((width, i) => (
            <Skeleton key={i} className={`h-7.5 ${width}`} />
          ))}
          <Separator orientation="vertical" className="h-5" />
        </div>
      </div>

      {/* rows fill the rest, clipped like the real scroll container */}
      <div className="min-h-0 flex-1 overflow-hidden">
        <div className="flex h-10 items-center gap-2.5 border-b px-2.5">
          <Skeleton className="size-4.25" />
          <Skeleton className="ml-1 h-3 w-20" />
          <div className="ml-auto flex items-center gap-2.5">
            {COLUMNS.map((column, i) => (
              <Skeleton key={i} className={`h-3 ${column.head} ${i > 4 ? "hidden xl:block" : i > 2 ? "hidden lg:block" : ""}`} />
            ))}
          </div>
        </div>
        {Array.from({ length: rows }, (_, i) => {
          const [handle, email] = ROW_SHAPES[i % ROW_SHAPES.length];
          return (
            <div key={i} className="flex h-14 items-center gap-2.5 border-b px-2.5">
              <Skeleton className="size-4.25 shrink-0" />
              <Skeleton className="ml-1 size-9.5 shrink-0 rounded-pill" />
              <div className="flex flex-col gap-1.5">
                <Skeleton className={`h-3.5 ${handle}`} />
                <Skeleton className={`h-3 ${email}`} />
              </div>
              <div className="ml-auto flex items-center gap-2.5">
                {COLUMNS.map((column, j) => (
                  <Skeleton
                    key={j}
                    className={`h-3.5 ${column.cell} ${j > 4 ? "hidden xl:block" : j > 2 ? "hidden lg:block" : ""}`}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* pagination footer */}
      <div className="flex shrink-0 items-center justify-between p-2 pl-3">
        <div className="flex items-center gap-3">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-24" />
        </div>
        <div className="flex items-center gap-1.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className={i === 2 ? "h-3 w-10" : "size-7"} />
          ))}
        </div>
      </div>
    </div>
  );
}
